/**
 * Engagement Insights — CBO-scoped analytics + ML insights composed for the
 * v2 Reports page.
 *
 * Scope: every endpoint normally returns CBO-scoped data. When the caller is
 * the internal Trust Marketplace CBO (configured via
 * LEGACY_TRUST_INTERNAL_CBO_ID on the backend, default 1), every endpoint
 * returns system-wide aggregates across ALL CBOs (including CBO 0 / CCHI).
 * The panel banner reflects whichever scope the backend reports.
 */

import React from 'react';
import { Globe2, Building2, ArrowLeft, MapPin } from 'lucide-react';
import { ReplyTrendCard } from './ReplyTrendCard';
import { CampaignPerformanceCard } from './CampaignPerformanceCard';
import { OptimalSendTimeHeatmapCard } from './OptimalSendTimeHeatmapCard';
import { DemographicEquityCard } from './DemographicEquityCard';
import { MemberLifecycleCard } from './MemberLifecycleCard';
import { CBOComparisonCard } from './CBOComparisonCard';
import { ProgramBreakdownCard } from './ProgramBreakdownCard';
import { OutreachVelocityCard } from './OutreachVelocityCard';
import { LanguageMagnitudeCard } from './LanguageMagnitudeCard';
import { InferredIntentCard } from './InferredIntentCard';
import { GeoHotspotsCard } from './GeoHotspotsCard';
import { GeoChoroplethMap } from './GeoChoroplethMap';
import { CollapsibleCard } from './CollapsibleCard';
import { FederationImpactCard } from './FederationImpactCard';
import { EnvironmentalImpactCard } from './EnvironmentalImpactCard';
import { ReplyIntentCard } from './ReplyIntentCard';
import { AnalyticsQACard } from './AnalyticsQACard';
import { OutcomeTrendCard } from './OutcomeTrendCard';
import { CrossCohortComparisonCard } from './CrossCohortComparisonCard';
import { ECMEnrollmentPriorityCard } from './ECMEnrollmentPriorityCard';
import { EngagementQualityCard } from './EngagementQualityCard';
import { DemoStoriesCard } from './DemoStoriesCard';
import { DrillDownProvider, useDrillDown } from './DrillDownContext';
import { useReplyTrend } from '@/hooks/ml';

const PanelInner: React.FC = () => {
  const { drillCboId, drillCboName, setDrill } = useDrillDown();
  const probe = useReplyTrend(30); // smallest endpoint — learn scope of caller (consolidated vs cbo)
  const isInternal = probe.data?.scope === 'consolidated';
  const drilledIn = drillCboId != null;

  return (
    <div className="space-y-4">
      {drilledIn ? (
        <div className="flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span>
              Drill-down: <span className="font-medium">{drillCboName ?? `CBO ${drillCboId}`}</span>
              {' '}<span className="text-amber-700/80">(id {drillCboId})</span>
            </span>
          </div>
          <button
            type="button"
            onClick={() => setDrill(null)}
            className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            <ArrowLeft className="h-3 w-3" /> Back to consolidated view
          </button>
        </div>
      ) : isInternal ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <Globe2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Consolidated view — all CBOs</div>
            <div className="text-amber-800/90">
              Aggregated across every CBO in the legacy Trust data, including
              the CCHI outreach pool. Pick a CBO below to drill in.
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
          <Building2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            Insights filtered to your CBO server-side — you cannot see data
            for other organizations.
          </div>
        </div>
      )}

      {/* Internal CBO only: Stories + Ask the data at the top — these are
          the entry-point cards. Federation impact, outcome trend, cohort
          comparison, and reply intent follow underneath. */}
      {isInternal && !drilledIn && (
        <>
          <CollapsibleCard
            id="demo-stories"
            title="Stories"
            defaultOpen
          >
            <DemoStoriesCard />
          </CollapsibleCard>

          <CollapsibleCard
            id="analytics-qa"
            title="Ask the data — Q&A over the federation metrics"
            defaultOpen
          >
            <AnalyticsQACard />
          </CollapsibleCard>

          <CollapsibleCard
            id="federation-impact"
            title="Federation impact — CCHI / MFOA outcomes & calibration"
            defaultOpen
          >
            <FederationImpactCard />
          </CollapsibleCard>

          <CollapsibleCard
            id="environmental-impact"
            title="Environmental impact — disaster exposure & engagement gap by federation"
            defaultOpen
          >
            <EnvironmentalImpactCard />
          </CollapsibleCard>

          <CollapsibleCard
            id="outcome-trend"
            title="Outcome trajectory — projected enrollments + nav supports over time"
            defaultOpen
          >
            <OutcomeTrendCard />
          </CollapsibleCard>

          <CollapsibleCard
            id="cross-cohort"
            title="Cross-cohort comparison — CCHI vs IFR vs Sycamores vs VyC"
            defaultOpen
          >
            <CrossCohortComparisonCard />
          </CollapsibleCard>

          <CollapsibleCard
            id="ecm-enrollment-priority"
            title="ECM enrollment priority — who to prioritise for outreach"
            defaultOpen
          >
            <ECMEnrollmentPriorityCard />
          </CollapsibleCard>

          <CollapsibleCard
            id="engagement-quality"
            title="Engagement quality — 6-component composite per federation"
            defaultOpen
          >
            <EngagementQualityCard />
          </CollapsibleCard>

          <CollapsibleCard
            id="reply-intent"
            title="Reply intent — what members said back"
            defaultOpen
          >
            <ReplyIntentCard />
          </CollapsibleCard>
        </>
      )}

      <CollapsibleCard id="engagement-over-time" title="Engagement (reply or click) over time" defaultOpen>
        <ReplyTrendCard />
      </CollapsibleCard>

      <CollapsibleCard id="outreach-velocity" title="Outreach velocity — weekly sends by language">
        <OutreachVelocityCard />
      </CollapsibleCard>

      <CollapsibleCard id="language-magnitude" title="Language magnitude & growth">
        <LanguageMagnitudeCard />
      </CollapsibleCard>

      <CollapsibleCard id="inferred-intent" title="Inferred intent of outreach">
        <InferredIntentCard />
      </CollapsibleCard>

      <CollapsibleCard id="geo-choropleth-map" title="Geographic distribution — US states (map)">
        <GeoChoroplethMap />
      </CollapsibleCard>

      <CollapsibleCard id="geo-hotspots" title="Geographic hotspots (table)">
        <GeoHotspotsCard />
      </CollapsibleCard>

      <CollapsibleCard id="send-timing" title="Best send time">
        <OptimalSendTimeHeatmapCard />
      </CollapsibleCard>

      <CollapsibleCard id="campaign-performance" title="Campaign performance">
        <CampaignPerformanceCard />
      </CollapsibleCard>

      <CollapsibleCard id="outreach-intent" title="Outreach intent — program breakdown">
        <ProgramBreakdownCard />
      </CollapsibleCard>

      <CollapsibleCard id="demographic-equity" title="Messages sent by language × age">
        <DemographicEquityCard />
      </CollapsibleCard>

      <CollapsibleCard id="member-lifecycle" title="Member life-cycle">
        <MemberLifecycleCard />
      </CollapsibleCard>

      {/* Internal CBO only: "Compare CBOs" at the bottom. The card hides
          itself when the /cbos endpoint returns 403 for non-internal. */}
      {isInternal && (
        <CollapsibleCard id="compare-cbos" title="Compare CBOs">
          <CBOComparisonCard />
        </CollapsibleCard>
      )}
    </div>
  );
};

export const EngagementInsightsPanel: React.FC = () => (
  <DrillDownProvider>
    <PanelInner />
  </DrillDownProvider>
);

export default EngagementInsightsPanel;
